import { useState } from 'react';
import appConstants from '../../constants';
import {getValidProp} from '../../utils/cleanValidateStringPropValue';

const NOTIFICATION_TYPE_INFO = 'info';
const NOTIFICATION_TYPE_SUCCESS = 'success';
const NOTIFICATION_TYPE_WARNING = 'warning';
const NOTIFICATION_TYPE_ALERT = 'alert';

const DELAY_INFINITE = -1;
const DELAY_MILLISECONDS_3000 = 3000;
const DELAY_MILLISECONDS_5000 = 5000;
const DELAY_MILLISECONDS_7000 = 7000;

const constants = Object.freeze({
  NOTIFICATION_TYPE_INFO: NOTIFICATION_TYPE_INFO,
  NOTIFICATION_TYPE_SUCCESS: NOTIFICATION_TYPE_SUCCESS,
  NOTIFICATION_TYPE_WARNING: NOTIFICATION_TYPE_WARNING,
  NOTIFICATION_TYPE_ALERT: NOTIFICATION_TYPE_ALERT,
  NOTIFICATION_TYPES: [
    NOTIFICATION_TYPE_INFO,
    NOTIFICATION_TYPE_SUCCESS,
    NOTIFICATION_TYPE_WARNING,
    NOTIFICATION_TYPE_ALERT
  ],
  NOTIFICATION_TYPE_DEFAULT: NOTIFICATION_TYPE_INFO,
  NOTIFICATION_AUTO_DISMISSAL_DELAY_INFINITE: DELAY_INFINITE,
  NOTIFICATION_AUTO_DISMISSAL_DELAY_MS_3000: DELAY_MILLISECONDS_3000,
  NOTIFICATION_AUTO_DISMISSAL_DELAY_MS_5000: DELAY_MILLISECONDS_5000,
  NOTIFICATION_AUTO_DISMISSAL_DELAY_MS_7000: DELAY_MILLISECONDS_7000,
  NOTIFICATION_AUTO_DISMISSAL_DELAYS: [
    DELAY_INFINITE,
    DELAY_MILLISECONDS_3000,
    DELAY_MILLISECONDS_5000,
    DELAY_MILLISECONDS_7000
  ],
  NOTIFICATION_AUTO_DISMISSAL_DELAY_DEFAULT: DELAY_INFINITE,
});

const defaults = Object.freeze({
  notify: {
    notificationText: undefined,
    notificationType: constants.NOTIFICATION_TYPE_DEFAULT,
    notificationShowIcon: true,
    notificationCaption: '',
    notificationActionLabel: '',
    notificationActionFn: null,
    notificationActionFnArgs: undefined,
    notificationDismissControlTitle: 'Dismiss',
    notificationDismissFn: null,
    notificationDismissFnArgs: undefined,
    notificationAutoDismissalDelay: constants.NOTIFICATION_AUTO_DISMISSAL_DELAY_INFINITE
  }
});


export default function useNotifications() {
  const [notificationList, setNotificationList] = useState([]);

  function dismissNotification(notification) {
    if (!!notification.dismissalTimerId) {
      clearTimeout(notification.dismissalTimerId);
    }
    setNotificationList(
      (notificationList) => [...notificationList.filter((n) => n.id !== notification.id)]
    );
  }

  function scheduleNotificationDismissal(notification, notificationDismissalDelay) {
     return setTimeout(dismissNotification, notificationDismissalDelay, notification);
  }

  function getValidNotificationType(notificationType) {
    return getValidProp(notificationType, 'type', constants.NOTIFICATION_TYPE_DEFAULT, constants.NOTIFICATION_TYPES, 'useNotifications', appConstants.APP_DEBUG_LOGGER);
  }

  function hasSimilarNotification(notificationText, notificationType) {
    const similarNotifications = notificationList.filter(notification => (notification.type === notificationType) && (notification.text === notificationText));
    return similarNotifications.length > 0;
  }

  function notify(
    notificationText = defaults.notify.notificationText,
    notificationType = defaults.notify.notificationType,
    notificationShowIcon= defaults.notify.notificationShowIcon,
    notificationCaption = defaults.notify.notificationCaption,
    notificationActionLabel = defaults.notify.notificationActionLabel,
    notificationActionFn= defaults.notify.notificationActionFn,
    notificationActionFnArgs= defaults.notify.notificationDismissFnArgs,
    notificationAutoDismissalDelay = defaults.notify.notificationAutoDismissalDelay,
    notificationDismissControlTitle= defaults.notify.notificationDismissControlTitle,
    notificationDismissFn= defaults.notify.notificationDismissFn,
    notificationDismissFnArgs= defaults.notify.notificationDismissFnArgs
  ) {
    if (!hasSimilarNotification(notificationText, notificationType)) {
      let newNotification = {
        id: Date.now(),
        showIcon: notificationShowIcon,
        type: getValidNotificationType(notificationType),
        text: notificationText,
        caption: notificationCaption,
        actionLabel: notificationActionLabel,
        actionFn: notificationActionFn,
        actionFnArgs: notificationActionFnArgs,
        autoDismissalDelay: notificationAutoDismissalDelay,
        dismissalTimerId: null,
        dismissControlTitle: notificationDismissControlTitle,
        dismissFn: notificationDismissFn,
        dismissFnArgs: notificationDismissFnArgs
      };

      if (!notificationDismissFn) {
        newNotification.dismissFn = dismissNotification;
        newNotification = {...newNotification, dismissFnArgs: newNotification };
      }
      if (notificationAutoDismissalDelay !== constants.NOTIFICATION_AUTO_DISMISSAL_DELAY_INFINITE) {
        newNotification.dismissalTimerId = scheduleNotificationDismissal(newNotification, notificationAutoDismissalDelay);
      }
      setNotificationList((notificationList) => [...notificationList, newNotification]);
      return newNotification;
    }
    else {
      return null;
    }
  }

  return { notificationList, notify, dismissNotification };
}
